import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import type { ApiError } from '@/api/errors'
import type {
  BloodGroup,
  CallerConvertResponse,
  CallerResponse,
  PatientWriteRequest,
} from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { Card } from '@/components/Card'
import { DateField } from '@/components/DateField'
import { Field } from '@/components/Field'
import { PhoneField } from '@/components/PhoneField'
import { Select } from '@/components/Select'
import { TextField } from '@/components/TextField'
import { useToast } from '@/components/Toast'
import { BLOOD_GROUP_OPTIONS, SEX_SELECT_OPTIONS } from '@/lib/clinical'
import { todayIso } from '@/lib/dates'
import { callers as api } from './api'
import { errorMessage } from '@/api/messages'

/**
 * Turning an enquiry into a patient.
 *
 * The point is what it does NOT ask for: everything the enquiry already holds
 * arrives filled in. One request does both writes, so a taken OP number leaves
 * the enquiry exactly as it was rather than closed against no patient.
 */
export function useConvertCaller({
  callerId,
  onConverted,
}: {
  callerId: string
  onConverted: (result: CallerConvertResponse) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const caller = useQuery<CallerResponse, ApiError>({
    queryKey: queryKeys.caller(callerId),
    queryFn: ({ signal }) => api.get(callerId, signal),
    enabled: Boolean(callerId),
  })

  const [form, setForm] = useState<PatientWriteRequest>({
    op_number: '',
    full_name: '',
    date_of_birth: null,
    age_years: null,
    sex: 'unknown',
    phone: null,
    address: null,
    blood_group: null,
  })

  // Carried over the moment the enquiry loads — this is the whole feature.
  useEffect(() => {
    const c = caller.data
    if (!c) return
    setForm((current) => ({
      ...current,
      full_name: c.full_name,
      phone: c.phone,
      // The place they rang from is the best first draft of an address.
      address: c.place,
    }))
  }, [caller.data])

  const convert = useMutation<CallerConvertResponse, ApiError>({
    mutationFn: () => api.convert(callerId, form),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['callers'] })
      await queryClient.invalidateQueries({ queryKey: queryKeys.caller(callerId) })
      toast.show(`${result.patient.full_name} is now a patient`)
      onConverted(result)
    },
    onError: (err) => setError(errorMessage(err)),
  })

  function update(changes: Partial<PatientWriteRequest>) {
    setForm((current) => ({ ...current, ...changes }))
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    // The OP number is the only thing a call cannot already supply, and the
    // only thing checked before the request. Age and date of birth are not
    // required: an age nobody is sure of, demanded to get past this screen,
    // is an age that gets guessed and then read as fact forever after.
    if (!form.op_number.trim()) {
      setError('An OP number is required.')
      return
    }
    convert.mutate()
  }

  return { caller, form, update, error, setError, convert, submit }
}

export type ConvertCallerState = ReturnType<typeof useConvertCaller>

function Group({
  title,
  chrome,
  children,
}: {
  title: string
  chrome: 'card' | 'plain'
  children: ReactNode
}) {
  if (chrome === 'card') return <Card title={title}>{children}</Card>
  return (
    <section className="[&>*+*]:mt-4">
      <h3 className="mb-3 text-2xs font-semibold tracking-wide text-fg-subtle uppercase">
        {title}
      </h3>
      {children}
    </section>
  )
}

export function ConvertCallerFields({
  state,
  chrome = 'card',
}: {
  state: ConvertCallerState
  chrome?: 'card' | 'plain'
}) {
  const { form, update } = state

  return (
    <>
      <Group title="Carried over from the call" chrome={chrome}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Name"
            required
            value={form.full_name}
            onChange={(e) => update({ full_name: e.target.value })}
          />
          <PhoneField
            label="Phone number"
            value={form.phone ?? ''}
            onChange={(phone) => update({ phone: phone || null })}
          />
        </div>
        <div>
          <TextField
            label="Address"
            hint="Started from the place they rang from — correct it if they gave a fuller one."
            value={form.address ?? ''}
            onChange={(e) => update({ address: e.target.value || null })}
          />
        </div>
      </Group>

      <Group title="What the clinic still needs" chrome={chrome}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="OP number"
            required
            autoFocus
            hint="Assigned by the clinic. A call never has one."
            value={form.op_number}
            onChange={(e) => update({ op_number: e.target.value })}
          />
          <DateField
            label="Date of birth"
            max={todayIso()}
            value={form.date_of_birth ?? ''}
            onChange={(iso) => update({ date_of_birth: iso || null, age_years: null })}
          />
          <TextField
            label="Age"
            type="number"
            min={0}
            max={130}
            hint="If the date of birth is not known."
            value={form.age_years ?? ''}
            disabled={Boolean(form.date_of_birth)}
            onChange={(e) =>
              update({ age_years: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <Field label="Sex">
            {({ id, labelId }) => (
              <Select
                id={id}
                labelledBy={labelId}
                options={SEX_SELECT_OPTIONS}
                value={form.sex}
                onChange={(sex) => update({ sex })}
              />
            )}
          </Field>
          <Field label="Blood group">
            {({ id, labelId }) => (
              <Select
                id={id}
                labelledBy={labelId}
                options={BLOOD_GROUP_OPTIONS}
                value={form.blood_group ?? ''}
                onChange={(group) => update({ blood_group: (group || null) as BloodGroup | null })}
              />
            )}
          </Field>
        </div>
      </Group>
    </>
  )
}
